
import { useState, useEffect, useRef, useCallback } from "react";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useAuth } from "@/hooks/useAuth";
import { validateEmail, validatePhone } from "@/lib/utils";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { OnboardingState } from "@/lib/types";

export function useOnboardingForm() {
  const {
    getCurrentStep,
    getCurrentSubStep,
    canProceed,
    proceedToNextStep,
    proceedToNextSubStep,
    proceedToNextDay,
    state,
    saveInput,
    getInput,
    resetOnboarding,
    isAdmin,
    isTodoCompleted,
    loadSavedState
  } = useOnboarding();

  const { 
    checkUserExists, 
    saveUserInfo,
    saveOnboardingProgress,
    user
  } = useAuth();

  const navigate = useNavigate();
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({});
  const [showDayCompleted, setShowDayCompleted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const currentStep = getCurrentStep();
  const currentSubStep = getCurrentSubStep();

  const inputsLoadedRef = useRef(false);
  
  useEffect(() => {
    if (currentStep && !inputsLoadedRef.current) {
      const values: Record<string, string> = {};
      
      if (currentStep.inputs) {
        currentStep.inputs.forEach(input => {
          values[input.name] = getInput(input.name);
        });
      }
      
      if (currentSubStep?.inputs) {
        currentSubStep.inputs.forEach(input => {
          values[input.name] = getInput(input.name);
        });
      }

      if (currentStep.conditionalInput) {
        const name = currentStep.conditionalInput.input.name;
        values[name] = getInput(name);
      }
      
      setInputValues(values);
      inputsLoadedRef.current = true;
      
      setTimeout(() => {
        const firstInputName = currentSubStep?.inputs?.[0]?.name || currentStep.inputs?.[0]?.name;
        if (firstInputName) {
          const inputElement = document.getElementById(firstInputName) as HTMLInputElement;
          if (inputElement) {
            inputElement.focus();
          }
        }
      }, 100);
    }
  }, [currentStep, currentSubStep, getInput]);
  
  useEffect(() => {
    inputsLoadedRef.current = false;
  }, [state.currentStepIndex, state.currentSubStepIndex]);

  const handleInputChange = useCallback((name: string, value: string, type: string) => {
    console.log(`Input change: ${name} = ${value} (${type})`);
    setInputValues(prev => ({ ...prev, [name]: value }));

    let error = '';
    if (value.trim() === '') {
      error = 'This field is required';
    } else if (type === 'email' && !validateEmail(value)) {
      error = 'Please enter a valid email address';
    } else if (type === 'phone' && !validatePhone(value)) {
      error = 'Please enter a valid phone number';
    }

    setInputErrors(prev => ({ ...prev, [name]: error }));

    if (!error) {
      saveInput(name, value);
    }
  }, [saveInput]);

  const handleNextStep = async () => {
    if (currentStep?.id === 'signup' && currentSubStep?.id === 'email') {
      setIsProcessing(true);
      try {
        const email = inputValues.email;
        
        if (!email || !validateEmail(email)) {
          toast.error("Please enter a valid email address");
          setIsProcessing(false);
          return;
        }
        
        saveInput('email', email);
        
        const { exists, progress } = await checkUserExists(email);
        
        if (exists && progress) {
          toast.success("Welcome back! Resuming from where you left off.");
          console.log("Found saved progress:", progress);
          
          resetOnboarding();
          
          loadSavedState(progress);
          
          setIsProcessing(false);
          return;
        } else {
          proceedToNextSubStep();
          if (exists) {
            toast.success("Welcome back! Proceeding with onboarding.");
          } else {
            toast.success("Let's get you set up!");
          }
        }
      } catch (error) {
        console.error("Error in email verification:", error);
        toast.error("Failed to process email verification");
      } finally {
        setIsProcessing(false);
      }
      return;
    }
    
    if (currentStep?.id === 'signup' && currentSubStep?.id === 'name') {
      proceedToNextSubStep();
      return;
    }
    
    if (currentStep?.id === 'signup' && currentSubStep?.id === 'whatsapp') {
      setIsProcessing(true);
      try {
        const email = getInput('email');
        const name = getInput('name');
        const whatsapp = inputValues.whatsapp;
        
        if (!email) {
          toast.error("Email is required");
          setIsProcessing(false);
          return;
        }
        
        console.log("Saving user info:", { email, name, whatsapp });
        
        const success = await saveUserInfo(email, name, whatsapp);
        
        if (success) {
          proceedToNextStep();
          toast.success("Your information has been saved successfully!");
        } else {
          toast.error("Failed to save your information");
        }
      } catch (error) {
        console.error("Error saving user information:", error);
        toast.error("Failed to save your information");
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    if (currentStep?.id === 'create-tiktok' && currentSubStep?.id === 'create') {
      if (isTodoCompleted(currentStep.id, 'account-created')) {
        setIsProcessing(true);
        try {
          const email = getInput('email');
          const username = inputValues.username;
          
          console.log("TikTok username submission:", username);
          
          if (!email) {
            toast.error("Email is required");
            setIsProcessing(false);
            return;
          }
          
          if (!username || username.trim() === "") {
            toast.error("Please enter your TikTok username");
            setIsProcessing(false);
            return;
          }
          
          const success = await saveUserInfo(email, undefined, undefined, username);
          
          if (success) {
            saveInput('username', username);
            proceedToNextStep();
            toast.success("Your TikTok username has been saved!");
          } else {
            toast.error("Failed to save your TikTok username");
          }
        } catch (error) {
          console.error("Error saving TikTok username:", error);
          toast.error("Failed to save your TikTok username");
        } finally {
          setIsProcessing(false);
        }
      } else {
        proceedToNextStep();
      }
      return;
    }

    if (currentStep?.type === 'multiStep' || 
        (currentStep?.type === 'dayStep' && currentSubStep)) {
      if (currentStep.type === 'dayStep' && 
          currentStep.days && 
          state.currentSubStepIndex === currentStep.days[state.currentDayIndex].subSteps.length - 1) {
        
        console.log(`Completing day ${state.currentDayIndex + 1}, showing completion screen`);
        
        const newState = {
          ...state,
          completedSubSteps: {
            ...state.completedSubSteps,
            [`${currentStep.id}_day`]: [
              ...(state.completedSubSteps[`${currentStep.id}_day`] || []),
              `day${state.currentDayIndex}`
            ]
          }
        };
        
        // Save progress to the server
        if (user?.email) {
          saveOnboardingProgress(user.email, newState);
        }
        
        setShowDayCompleted(true);
        return;
      }
      
      proceedToNextSubStep();
    } else {
      proceedToNextStep();
    }
  };

  const continueToNextDay = () => {
    console.log(`Moving from day ${state.currentDayIndex + 1} to day ${state.currentDayIndex + 2}`);
    setShowDayCompleted(false);
    
    const newDayIndex = state.currentDayIndex + 1;
    if (currentStep?.type === 'dayStep' && currentStep.days && newDayIndex < currentStep.days.length) {
      const newState = {
        ...state,
        currentDayIndex: newDayIndex,
        currentSubStepIndex: 0
      };
      
      // Save progress to the server
      if (user?.email) {
        saveOnboardingProgress(user.email, newState);
      }
    }
    
    proceedToNextDay();
  };

  const handleReset = () => {
    if (window.confirm("This will reset your onboarding progress. Are you sure?")) {
      resetOnboarding();
      toast.success("Onboarding progress has been reset");
    }
  };

  return {
    currentStep,
    currentSubStep,
    state,
    inputValues,
    inputErrors,
    showDayCompleted,
    isProcessing,
    isAdmin,
    canProceed,
    handleInputChange,
    handleNextStep,
    continueToNextDay,
    handleReset,
    isTodoCompleted,
    loadSavedState
  };
}
